import { Component, effect, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { routerCommands, toLang } from '../seo/seo.config';
import { ConsentService } from '../services/consent.service';
import { LanguageService } from '../services/language.service';

/**
 * The cookie banner (decision D12, 12/10/2026; Garante's cookie guidelines, 2021):
 * - nothing but technical cookies until the visitor chooses;
 * - «Rifiuta» as visible as «Accetta tutto», and the close button keeps the technical ones only;
 * - «Scegli» opens one switch per tool;
 * - «Preferenze cookie» in the footer brings it back at any time.
 * It renders in the browser only (ConsentService keeps it closed on the server).
 */
@Component({
  selector: 'app-cookie-banner',
  imports: [RouterLink, TranslateModule],
  template: `
    @if (consent.bannerOpen()) {
      <section class="cookie-banner" role="dialog" aria-labelledby="cookie-banner-title">
        <button
          type="button"
          class="cookie-banner__close"
          [attr.aria-label]="'cookies.close' | translate"
          (click)="consent.rejectAll()"
        >
          &times;
        </button>
        <h2 id="cookie-banner-title">{{ 'cookies.title' | translate }}</h2>
        <p>
          {{ 'cookies.text' | translate }}
          <a [routerLink]="privacyLink" [fragment]="cookieAnchor">{{ 'cookies.policy' | translate }}</a>
        </p>
        @if (choosing()) {
          <ul class="cookie-banner__list">
            <li>
              <label>
                <input type="checkbox" checked disabled />
                <span><strong>{{ 'cookies.technical.title' | translate }}</strong> {{ 'cookies.technical.text' | translate }}</span>
              </label>
            </li>
            <li>
              <label>
                <input type="checkbox" [checked]="google()" (change)="google.set(checked($event))" />
                <span><strong>{{ 'cookies.google.title' | translate }}</strong> {{ 'cookies.google.text' | translate }}</span>
              </label>
            </li>
            <li>
              <label>
                <input type="checkbox" [checked]="meta()" (change)="meta.set(checked($event))" />
                <span><strong>{{ 'cookies.meta.title' | translate }}</strong> {{ 'cookies.meta.text' | translate }}</span>
              </label>
            </li>
          </ul>
        }
        <div class="cookie-banner__actions">
          <button type="button" class="btn btn-primary btn-sm" (click)="consent.rejectAll()">
            {{ 'cookies.reject' | translate }}
          </button>
          @if (choosing()) {
            <button type="button" class="btn btn-outline btn-sm" (click)="saveChoice()">
              {{ 'cookies.save' | translate }}
            </button>
          } @else {
            <button type="button" class="btn btn-outline btn-sm" (click)="choose()">
              {{ 'cookies.choose' | translate }}
            </button>
          }
          <button type="button" class="btn btn-primary btn-sm" (click)="consent.acceptAll()">
            {{ 'cookies.accept' | translate }}
          </button>
        </div>
      </section>
    }
  `,
})
export class CookieBannerComponent {
  protected readonly choosing = signal(false);
  protected readonly google = signal(false);
  protected readonly meta = signal(false);

  constructor(
    protected readonly consent: ConsentService,
    private readonly lang: LanguageService,
  ) {
    // Reopened from «Preferenze cookie»: show the switches with the choice already made.
    effect(() => {
      if (this.consent.bannerOpen() && this.consent.choice()) this.choose();
    });
  }

  protected get privacyLink(): string[] {
    return routerCommands('privacy', toLang(this.lang.current));
  }

  /** The cookie section of the privacy page, as the footer's «Cookie» link. */
  protected get cookieAnchor(): string {
    return `${toLang(this.lang.current)}-cookie`;
  }

  /** The switches start from the saved choice: nothing on at the first visit. */
  protected choose(): void {
    this.google.set(this.consent.granted('google'));
    this.meta.set(this.consent.granted('meta'));
    this.choosing.set(true);
  }

  protected saveChoice(): void {
    this.consent.save({ google: this.google(), meta: this.meta() });
    this.choosing.set(false);
  }

  protected checked(e: Event): boolean {
    return (e.target as HTMLInputElement).checked;
  }
}
